/**
 * A brand the base stands in two stretches is two brands when the products say so.
 *
 * "Tide sits in 2 separate stretches" is a violation the tree can never clear on its own: one
 * brand value is one block. When a detached stretch is all one value of a level below the brand
 * (Simply, under Tide) and that value lives mostly there, the base is telling us that value is
 * merchandised as a brand of its own. The finding then offers the reclassification: its SKUs
 * take the brand "Tide Simply" through `attrOverrides`, so the tree, finalize and compression all
 * see just another brand. When no attribute says so, the product names can ("all Baby Liquid
 * Laundry Detergent"). A stretch of one or two products is only a brand line when the product
 * beside it, from another brand's detached stretch, says the same thing (all Baby beside
 * ARM & HAMMER Baby). A stretch nothing explains is a genuine scatter and keeps the plain
 * stretches violation.
 */
import {
  type FixAction,
  type Flag,
  type FlagQuestion,
  PATH_SEPARATOR,
  type StudioDraft,
  type StudioItem,
  type StudioRunGeometry,
  effectiveAttrs,
  isPlaceholderValue,
  levelsForScope,
} from "../domain/studioTypes";
import { basePogWalk, placementBayOf } from "../domain/basePogWalk";
import type { ResolverPin } from "./cdtResolve";
import { baseSkuId, placementIndexOf } from "./toResolverInputs";
import { planSlacksOf } from "../domain/bayShare";

/** Below this, a stretch is a stray for the relocate fix unless its neighbour vouches for it. */
export const MIN_SPLIT_OFF_SKUS = 3;

export interface BrandSplitOffContext {
  items: readonly StudioItem[];
  runs: readonly StudioRunGeometry[];
  draft: StudioDraft;
  brandAttr: string;
  /** Never names a brand: "Tide 92 fl oz" is a size, not a line. */
  quantityAttr?: string;
  /** The levels that shape blocks (section, form, pack type, the brand itself). They never name
   *  a line: "Gain Beads" is a form, not a brand. A product-name word equal to one of their
   *  values on the stretch's products names nothing either. */
  structuralAttrs?: readonly string[];
  /** Flag id → plan ids per stretch, as `detectCore` measured them. */
  stretches: Readonly<Record<string, readonly (readonly string[])[]>>;
  /** Offers the merchant answered No to, by flag id. A declined offer is still raised, marked
   *  `question.declined` so the panel can show it again, but it explains nothing: the plain row,
   *  its pin and the stray rows of its products all stay. */
  declinedOfferIds?: ReadonlySet<string>;
}

/** What a detached stretch's products have in common: an attribute value or a product-name word. */
type Evidence = { source: "attr"; attr: string; value: string } | { source: "name"; value: string };

interface Voucher {
  brand: string;
  value: string;
  byName: boolean;
  skuId: string;
}

interface DetachedStretch {
  skus: string[];
  /** Every piece of evidence the stretch carries, attributes in tree order, then name words. */
  candidates: Evidence[];
}

interface BrandStretches {
  flag: Flag;
  brand: string;
  main: Set<string>;
  /** The main stretch's own placements, for "the rest of Tide is in bays 1–5". */
  mainPlacements: Placed[];
  detached: DetachedStretch[];
}

interface Explained {
  evidence: Evidence;
  skuIds: string[];
  stretchCount: number;
  vouchers: Voucher[];
}

/** A brand's block as the base stands it: its main stretch, or all of it when it is one block. */
interface BrandBlock {
  scopePath: readonly string[];
  brand: string;
  placed: { skuId: string; placement: Placed }[];
  levelLabel?: string;
}

/** A line found standing as a column of its own at one edge of a brand's block. */
interface EdgeColumn {
  attr: string;
  value: string;
  skuIds: string[];
  shelves: number;
  shelfCount: number;
  /** The line's products inside the rest of the block, which stay with the brand. */
  leftOut: number;
  column: Placed[];
  rest: Placed[];
}

export const splitOffBrandName = (brand: string, value: string): string =>
  value.toLowerCase().startsWith(brand.toLowerCase()) ? value : `${brand} ${value}`;

/** Sizes and counts say nothing about the line: "73 oz", "58 Loads", "80 Count". */
const UNIT_WORDS = new Set([
  "oz",
  "fl",
  "floz",
  "lb",
  "lbs",
  "ct",
  "count",
  "load",
  "loads",
  "ml",
  "l",
  "g",
  "kg",
  "qt",
  "gal",
  "pk",
  "pack",
]);

/** The words of a product name after its brand, in order: "all Baby Liquid, 73 oz" → Baby, Liquid.
 *  The brand is left out only as a whole word: "Allergen Free" under all keeps "Allergen". */
export const productNameWords = (name: string, brand: string): string[] => {
  const trimmed = name.trim();
  const startsWithBrand =
    brand !== "" &&
    trimmed.toLowerCase().startsWith(brand.toLowerCase()) &&
    !/[\p{L}\p{N}]/u.test(trimmed.charAt(brand.length));
  const rest = startsWithBrand ? trimmed.slice(brand.length) : trimmed;
  return rest
    .split(/[^\p{L}\p{N}']+/u)
    .map((word) => word.replace(/^'+|'+$/g, ""))
    .filter(
      (word) => /\p{L}/u.test(word) && !/\p{N}/u.test(word) && !UNIT_WORDS.has(word.toLowerCase()),
    );
};

const rangeText = (numbers: readonly number[]): string => {
  const sorted = [...new Set(numbers)].sort((a, b) => a - b);
  const parts: string[] = [];
  for (let index = 0; index < sorted.length; index += 1) {
    let end = index;
    while (end + 1 < sorted.length && sorted[end + 1] === sorted[end] + 1) end += 1;
    parts.push(index === end ? `${sorted[index]}` : `${sorted[index]}–${sorted[end]}`);
    index = end;
  }
  return parts.join(", ");
};

type Placed = StudioItem["placements"][number];

const bayLocations = (
  placements: readonly Placed[],
  runs: readonly StudioRunGeometry[],
): FlagQuestion["where"] => {
  const baysByRun = new Map<number, number[]>();
  for (const placement of placements) {
    // The bay the planogram authored: bays of different widths make x ÷ first bay width wrong.
    const bay = placementBayOf(
      { bay: placement.bay, xCm: placement.xCm + placement.widthCm / 2 },
      runs.find((entry) => entry.run === placement.run),
    );
    baysByRun.set(placement.run, [...(baysByRun.get(placement.run) ?? []), bay]);
  }
  return [...baysByRun.entries()]
    .sort(([a], [b]) => a - b)
    .map(([run, bays]) => ({
      bays: [...new Set(bays)].sort((a, b) => a - b),
      ...(runs.length > 1
        ? { run: { number: run + 1, name: runs.find((entry) => entry.run === run)?.name } }
        : {}),
    }));
};

const baysText = (placements: readonly Placed[], runs: readonly StudioRunGeometry[]): string =>
  bayLocations(placements, runs)
    .map(({ run, bays }) => {
      const prefix = run ? `${run.name ?? `run ${run.number}`} ` : "";
      const range = rangeText(bays);
      return `${prefix}${range.includes(",") ? "bays" : "bay"} ${range}`;
    })
    .join(" and ");

const whereText = (
  skuIds: readonly string[],
  items: ReadonlyMap<string, StudioItem>,
  runs: readonly StudioRunGeometry[],
): string =>
  baysText(
    skuIds.flatMap((skuId) => items.get(skuId)?.placements ?? []),
    runs,
  );

/** The products directly left and right of each product on its shelf, same run. */
const shelfNeighbours = (items: readonly StudioItem[]): Map<string, Set<string>> => {
  const shelves = new Map<string, { skuId: string; xCm: number }[]>();
  for (const item of items) {
    for (const placement of item.placements) {
      const key = `${placement.run}|${placement.shelf}`;
      shelves.set(key, [...(shelves.get(key) ?? []), { skuId: item.skuId, xCm: placement.xCm }]);
    }
  }
  const out = new Map<string, Set<string>>();
  const link = (a: string, b: string) => {
    out.set(a, (out.get(a) ?? new Set<string>()).add(b));
    out.set(b, (out.get(b) ?? new Set<string>()).add(a));
  };
  for (const shelf of shelves.values()) {
    shelf.sort((a, b) => a.xCm - b.xCm);
    for (let index = 1; index < shelf.length; index += 1) {
      if (shelf[index].skuId !== shelf[index - 1].skuId)
        link(shelf[index].skuId, shelf[index - 1].skuId);
    }
  }
  return out;
};

const sameWord = (a: Evidence, b: Evidence): boolean =>
  a.value.toLowerCase() === b.value.toLowerCase();

const products = (n: number): string => (n === 1 ? "product" : "products");

type RetagFix = Extract<FixAction, { type: "retagItems" }>;

/** A split-off offer the merchant has not declined: the question it asks and the brand it gives
 *  the products. */
const isOffer = (
  flag: Flag,
): flag is Flag & { question: FlagQuestion; fix: RetagFix & { value: string } } =>
  flag.id.startsWith("split-off:") &&
  flag.question !== undefined &&
  flag.question.declined !== true &&
  flag.fix?.type === "retagItems" &&
  flag.fix.value !== null;

const offerIdOf = (scopePath: readonly string[], newBrand: string): string =>
  `split-off:${scopePath.join(PATH_SEPARATOR)}:${newBrand}`;

/** The smallest share of a line's products that must stand in its edge column. */
const EDGE_COLUMN_SHARE = 2 / 3;
/** A level with fewer values than this across the planogram is a property, not a line. */
const MIN_LINE_VALUES = 3;

export const splitOffBrands = (
  flags: readonly Flag[],
  pinByFlagId: Readonly<Record<string, ResolverPin>>,
  context: BrandSplitOffContext,
): { flags: Flag[]; pinByFlagId: Record<string, ResolverPin> } => {
  const { items, runs, draft, brandAttr, quantityAttr, stretches } = context;
  const fit = planSlacksOf(runs).fit;
  const structural = new Set(context.structuralAttrs ?? []);
  const declinedIds = context.declinedOfferIds ?? new Set<string>();
  const bySku = new Map(items.map((item) => [item.skuId, item]));
  const attrsOf = (skuId: string) => {
    const item = bySku.get(skuId);
    return item ? effectiveAttrs(item, draft) : undefined;
  };
  /** Every brand value a product carries, so a split-off never names an existing brand. */
  const brandValues = new Set(items.map((item) => String(attrsOf(item.skuId)?.[brandAttr] ?? "")));
  /** The levels that name a stretch standing apart from its brand. A level with fewer than three
   *  values across the planogram is a property (Scented / Unscented, HE / non-HE), and a property
   *  never names a brand line. The edge-of-block rule reads yes/no levels its own way
   *  (`yesNoCovered`). */
  const valuesByAttr = new Map<string, Set<string>>();
  for (const item of items) {
    for (const [attr, value] of Object.entries(attrsOf(item.skuId) ?? {})) {
      const text = String(value ?? "");
      if (!text || isPlaceholderValue(text)) continue;
      valuesByAttr.set(attr, (valuesByAttr.get(attr) ?? new Set<string>()).add(text));
    }
  }
  const namesLines = (attr: string) => (valuesByAttr.get(attr)?.size ?? 0) >= MIN_LINE_VALUES;
  /** Item-master order, so an offer lists its products the way the planogram loaded them. */
  const itemRank = new Map(items.map((item, index) => [item.skuId, index]));
  /** One placement's width on the shelf, all its facings. */
  const facingsWidthOf = (planId: string): number => {
    const item = bySku.get(baseSkuId(planId));
    const placement = item?.placements[placementIndexOf(planId)];
    if (!item || !placement) return 0;
    return placement.widthCm || item.facingWidthCm * placement.hFacings;
  };
  const labelOf = (attr: string) => draft.spine.find((level) => level.attr === attr)?.label ?? attr;
  const wordsCache = new Map<string, Set<string>>();
  const nameWords = (skuId: string, brand: string): Set<string> => {
    let words = wordsCache.get(skuId);
    if (!words) {
      words = new Set(
        productNameWords(bySku.get(skuId)?.name ?? "", brand).map((word) => word.toLowerCase()),
      );
      wordsCache.set(skuId, words);
    }
    return words;
  };

  // First pass: every brand in stretches, its main stretch, and what each detached stretch carries.
  const measuredBrands = new Map<string, BrandStretches>();
  for (const flag of flags) {
    const measured = flag.kind === "split" ? stretches[flag.id] : undefined;
    const brand = flag.scopePath[flag.scopePath.length - 1];
    if (!measured || measured.length < 2 || !brand) continue;
    // Only the brand's own block: every member carries this brand value.
    if (flag.skuIds.some((skuId) => String(attrsOf(skuId)?.[brandAttr] ?? "") !== brand)) continue;

    // The main stretch is strictly the widest by facings; two equally wide stretches leave no
    // main block to keep, so the brand gets no offer and the plain row stays.
    const widths = measured.map((ids) => ids.reduce((sum, id) => sum + facingsWidthOf(id), 0));
    const mainIndex = widths.reduce(
      (best, width, index) => (width > widths[best] ? index : best),
      0,
    );
    if (widths.some((width, index) => index !== mainIndex && widths[mainIndex] - width < fit))
      continue;
    // A SKU faced in the main stretch belongs to it; the rest go to the first stretch they stand in.
    const stretchSkus = measured.map((ids) => [...new Set(ids.map(baseSkuId))]);
    const main = new Set(stretchSkus[mainIndex]);
    const claimed = new Set(main);
    const detachedSkus = stretchSkus.flatMap((skus, index) => {
      if (index === mainIndex) return [];
      const own = skus.filter((skuId) => !claimed.has(skuId));
      own.forEach((skuId) => claimed.add(skuId));
      return own.length > 0 ? [own] : [];
    });

    const levels = levelsForScope(draft.spine, draft.scopedLevelOrder, flag.scopePath);
    const brandIndex = levels.findIndex((level) => level.attr === brandAttr);
    const lower = (brandIndex < 0 ? [] : levels.slice(brandIndex + 1))
      .map((level) => level.attr)
      .filter((attr) => attr !== quantityAttr && !structural.has(attr) && namesLines(attr));

    const detached = detachedSkus.map((skus): DetachedStretch => {
      const candidates: Evidence[] = [];
      // An attribute value the products share and the main stretch never carries says what the
      // line is; a product-name word may not then name it something else.
      let ownValue = false;
      for (const attr of lower) {
        const values = new Set(skus.map((skuId) => String(attrsOf(skuId)?.[attr] ?? "")));
        if (values.size !== 1) continue;
        const [value] = values;
        if (isPlaceholderValue(value)) continue;
        // The value must live here: a stretch of what the main stretch mostly holds is scatter.
        const inMain = [...main].filter((skuId) => attrsOf(skuId)?.[attr] === value).length;
        if (inMain === 0) ownValue = true;
        if (inMain >= skus.length) continue;
        candidates.push({ source: "attr", attr, value });
      }
      // A lone product shares its values with no other product in its stretch, so they say
      // nothing against its name; its neighbour has to vouch for it in any case.
      const namesMayExplain = skus.length < 2 || !ownValue;
      // A word that is a section, form or pack type of these products names no line ("Beads").
      const structuralWords = new Set(
        [...structural].flatMap((attr) =>
          skus.map((skuId) => String(attrsOf(skuId)?.[attr] ?? "").toLowerCase()),
        ),
      );
      // The same test on the words of the product names, in the first product's word order.
      const seen = new Set<string>();
      const words = namesMayExplain ? productNameWords(bySku.get(skus[0])?.name ?? "", brand) : [];
      for (const word of words) {
        const key = word.toLowerCase();
        if (seen.has(key) || structuralWords.has(key)) continue;
        seen.add(key);
        if (!skus.every((skuId) => nameWords(skuId, brand).has(key))) continue;
        const inMain = [...main].filter((skuId) => nameWords(skuId, brand).has(key)).length;
        if (inMain >= skus.length) continue;
        candidates.push({ source: "name", value: word });
      }
      return { skus, candidates };
    });
    const mainPlacements = measured[mainIndex].flatMap((planId) => {
      const placement = bySku.get(baseSkuId(planId))?.placements[placementIndexOf(planId)];
      return placement ? [placement] : [];
    });
    measuredBrands.set(flag.id, {
      flag,
      brand,
      main,
      mainPlacements,
      detached,
    });
  }

  // Which brand's detached stretch each product stands in; main stretches and whole brands vouch
  // for nobody.
  const detachedOf = new Map<string, { brand: string; stretch: DetachedStretch }>();
  for (const { brand, detached } of measuredBrands.values()) {
    for (const stretch of detached) {
      for (const skuId of stretch.skus) detachedOf.set(skuId, { brand, stretch });
    }
  }
  const neighbours =
    [...measuredBrands.values()].some(({ detached }) =>
      detached.some((stretch) => stretch.skus.length < MIN_SPLIT_OFF_SKUS),
    ) && detachedOf.size > 0
      ? shelfNeighbours(items)
      : new Map<string, Set<string>>();

  /** Detached stretches in the order their brands were found, so a pair has a first and second. */
  const stretchOrder = new Map(
    [...measuredBrands.values()]
      .flatMap(({ detached }) => detached)
      .map((stretch, index) => [stretch, index]),
  );

  /**
   * The evidence a stretch is split off by on its own: its first attribute value, else its first
   * name word. A small stretch has none until a neighbour vouches for it, so every candidate it
   * carries may be the one.
   */
  const ownEvidence = (stretch: DetachedStretch): Evidence[] => {
    if (stretch.skus.length < MIN_SPLIT_OFF_SKUS) return stretch.candidates;
    const evidence =
      stretch.candidates.find((candidate) => candidate.source === "attr") ??
      stretch.candidates.find((candidate) => candidate.source === "name");
    return evidence ? [evidence] : [];
  };

  /** The first piece of this stretch's evidence a neighbour from another brand's stretch is
   *  split off by: a neighbour named "Y Fresh" vouches for no "X Baby", whatever else it says. */
  const firstVoucher = (
    brand: string,
    stretch: DetachedStretch,
  ): {
    evidence: Evidence;
    voucher: Voucher;
    other: { brand: string; stretch: DetachedStretch };
  } | null => {
    for (const evidence of stretch.candidates) {
      for (const skuId of stretch.skus) {
        for (const neighbour of neighbours.get(skuId) ?? []) {
          const other = detachedOf.get(neighbour);
          if (!other || other.brand === brand) continue;
          const match = ownEvidence(other.stretch).find((candidate) =>
            sameWord(candidate, evidence),
          );
          if (match) {
            const byName = match.source === "name";
            return {
              evidence,
              voucher: { brand: other.brand, value: match.value, byName, skuId: neighbour },
              other,
            };
          }
        }
      }
    }
    return null;
  };

  /**
   * The evidence a small stretch is split off by, and the neighbour that vouches for it. Two
   * small stretches that vouch for each other say one word: each taking its own first word the
   * other shares would split "Baby Fresh" beside "Fresh Baby" into Baby and Fresh. The pair
   * takes the first word, in the first stretch's order, that the second also carries.
   */
  const vouched = (
    brand: string,
    stretch: DetachedStretch,
  ): { evidence: Evidence; voucher: Voucher } | null => {
    const found = firstVoucher(brand, stretch);
    if (!found) return null;
    const { evidence, voucher, other } = found;
    const back =
      other.stretch.skus.length < MIN_SPLIT_OFF_SKUS
        ? firstVoucher(other.brand, other.stretch)
        : null;
    if (!back || back.other.stretch !== stretch || sameWord(back.evidence, evidence))
      return { evidence, voucher };
    const [first, second] =
      (stretchOrder.get(stretch) ?? 0) < (stretchOrder.get(other.stretch) ?? 0)
        ? [stretch, other.stretch]
        : [other.stretch, stretch];
    const word = first.candidates.find((candidate) =>
      second.candidates.some((entry) => sameWord(entry, candidate)),
    );
    const own = word && stretch.candidates.find((candidate) => sameWord(candidate, word));
    const theirs = word && other.stretch.candidates.find((candidate) => sameWord(candidate, word));
    if (!own || !theirs) return { evidence, voucher };
    return {
      evidence: own,
      voucher: { ...voucher, value: theirs.value, byName: theirs.source === "name" },
    };
  };

  const pins = { ...pinByFlagId };
  const out: Flag[] = [];
  for (const flag of flags) {
    const measured = measuredBrands.get(flag.id);
    if (!measured) {
      out.push(flag);
      continue;
    }
    const { brand, main, mainPlacements, detached } = measured;

    const explainedBy = new Map<string, Explained>();
    let unexplained = 0;
    for (const stretch of detached) {
      let found: { evidence: Evidence; voucher?: Voucher } | null = null;
      if (stretch.skus.length >= MIN_SPLIT_OFF_SKUS) {
        // Attributes first, exactly as before; the product names only when no attribute says so.
        const [evidence] = ownEvidence(stretch);
        if (evidence) found = { evidence };
      } else {
        found = vouched(brand, stretch);
      }
      if (!found) {
        unexplained += 1;
        continue;
      }
      // One new brand is one offer: an attribute value and a product-name word that spell the
      // same brand ("Simply") merge, the attribute leading.
      const key = splitOffBrandName(brand, found.evidence.value).toLowerCase();
      const entry = explainedBy.get(key) ?? {
        evidence: found.evidence,
        skuIds: [],
        stretchCount: 0,
        vouchers: [],
      };
      if (entry.evidence.source === "name" && found.evidence.source === "attr")
        entry.evidence = found.evidence;
      entry.skuIds.push(...stretch.skus);
      entry.stretchCount += 1;
      if (found.voucher) entry.vouchers.push(found.voucher);
      explainedBy.set(key, entry);
    }

    if (explainedBy.size === 0) {
      out.push(flag);
      continue;
    }
    const offers = [...explainedBy.values()].map((explained) => ({
      explained,
      declined: declinedIds.has(
        offerIdOf(flag.scopePath, splitOffBrandName(brand, explained.evidence.value)),
      ),
    }));
    // Still a scatter after the split-off, or an offer the merchant declined: the plain finding
    // and its pin stay beside the offers.
    if (unexplained > 0 || offers.some((offer) => offer.declined)) out.push(flag);
    else delete pins[flag.id];

    for (const { explained, declined } of offers) {
      out.push(
        splitOffFlag(flag, brand, main, bayLocations(mainPlacements, runs), explained, declined),
      );
    }
  }
  // The second shape of the same thing: a line standing as a column of its own at an edge of its
  // brand's block, touching the rest of the brand (Tide Free & Gentle after regular Tide). No
  // stretches row is raised for it, so it is read straight off each brand's main block.
  for (const group of brandBlocks()) {
    for (const offer of edgeOffersOf(group)) {
      if (!out.some((flag) => flag.id === offer.id)) out.push(offer);
    }
  }

  return { flags: withBrandOrder(out), pinByFlagId: pins };

  /** A placement's width on the shelf, all its facings. */
  function placedWidth(entry: { skuId: string; placement: Placed }): number {
    return (
      entry.placement.widthCm ||
      (bySku.get(entry.skuId)?.facingWidthCm ?? 0) * entry.placement.hFacings
    );
  }

  /**
   * Each brand's block as the base planogram carries it: every product the item master gives
   * this brand, split-offs already made included, so an edge is still the edge the base shows
   * after a Yes (Power PODS behind Tide Evo is not Tide's edge once Evo is its own brand). Where
   * the brand stands in several stretches, the widest one is its block.
   */
  function brandBlocks(): BrandBlock[] {
    const levels = levelsForScope(draft.spine, draft.scopedLevelOrder, []);
    const brandIndex = levels.findIndex((level) => level.attr === brandAttr);
    const above = brandIndex < 0 ? [] : levels.slice(0, brandIndex).map((level) => level.attr);
    const labelByScope = new Map(
      [...measuredBrands.values()].map(({ flag }) => [
        flag.scopePath.join(PATH_SEPARATOR),
        flag.levelLabel,
      ]),
    );
    const groups = new Map<string, BrandBlock>();
    for (const item of items) {
      if (item.placements.length === 0) continue;
      const brand = String(item.attrs[brandAttr] ?? "");
      if (!brand || isPlaceholderValue(brand)) continue;
      const attrs = attrsOf(item.skuId);
      const section = above
        .map((attr) => String(attrs?.[attr] ?? "").trim())
        .filter((value) => value.length > 0);
      const scopePath = [...section, brand];
      const key = scopePath.join(PATH_SEPARATOR);
      const group = groups.get(key) ?? {
        scopePath,
        brand,
        placed: [],
        levelLabel: labelByScope.get(key),
      };
      for (const placement of item.placements) group.placed.push({ skuId: item.skuId, placement });
      groups.set(key, group);
    }
    return [...groups.values()].map(widestStretchOf);
  }

  /** The brand's widest stretch: its placements, run by run, clustered until a gap of more than
   *  half a bay with none of the brand on any shelf. */
  function widestStretchOf(group: BrandBlock): BrandBlock {
    const byRun = new Map<number, BrandBlock["placed"]>();
    for (const entry of group.placed)
      byRun.set(entry.placement.run, [...(byRun.get(entry.placement.run) ?? []), entry]);
    let best: { placed: BrandBlock["placed"]; width: number } | null = null;
    for (const [run, placed] of byRun) {
      const bayWidth = runs.find((entry) => entry.run === run)?.bayWidthCm ?? Infinity;
      const spans = placed
        .map((entry) => ({
          entry,
          x0: entry.placement.xCm,
          x1: entry.placement.xCm + placedWidth(entry),
        }))
        .sort((a, b) => a.x0 - b.x0);
      const stretches: { x0: number; x1: number; placed: BrandBlock["placed"] }[] = [];
      for (const span of spans) {
        const last = stretches[stretches.length - 1];
        if (last && span.x0 <= last.x1 + bayWidth / 2) {
          last.x1 = Math.max(last.x1, span.x1);
          last.placed.push(span.entry);
        } else stretches.push({ x0: span.x0, x1: span.x1, placed: [span.entry] });
      }
      for (const stretch of stretches) {
        const width = stretch.x1 - stretch.x0;
        if (!best || width > best.width) best = { placed: stretch.placed, width };
      }
    }
    return { ...group, placed: best?.placed ?? [] };
  }

  /**
   * A line standing as a column of its own at the left or right edge of the brand's block: it
   * holds that edge on more than half the shelves the block touches, most of its products stand
   * in the column, the column is well under half the block, and it is only a part of the brand.
   * The column is each shelf's run of the line's products from the edge, so it may reach into the
   * next bay where the line starts mid-bay (Tide's white liquids: the last product of each bay 4
   * shelf, then bay 5). Of every level and value standing at the edge this way, the one whose
   * column holds the most products names the line, and on a tie the level that comes first. On
   * base 210 scented "Unscented" and free/clear "Free & Clear" both hold the 14 white liquids and
   * scented comes first, so the line is "Tide Unscented"; the sub-brand "Free & Gentle" holds 7 of
   * them. The column's products are the offer; the line's products inside the rest of the block
   * stay, as the detached rule keeps them.
   */
  function edgeOffersOf(group: BrandBlock): Flag[] {
    const { scopePath, brand, placed } = group;
    if (placed.length === 0) return [];
    const runCounts = new Map<number, number>();
    for (const entry of placed)
      runCounts.set(entry.placement.run, (runCounts.get(entry.placement.run) ?? 0) + 1);
    const run = [...runCounts.entries()].sort((a, b) => b[1] - a[1])[0][0];
    const inRun = placed
      .filter((entry) => entry.placement.run === run)
      .map((entry) => ({
        skuId: entry.skuId,
        placement: entry.placement,
        shelf: entry.placement.shelf,
        x0: entry.placement.xCm,
        x1:
          entry.placement.xCm +
          (entry.placement.widthCm ||
            (bySku.get(entry.skuId)?.facingWidthCm ?? 0) * entry.placement.hFacings),
      }));
    const brandSkus = new Set(inRun.map((entry) => entry.skuId)).size;
    const left = Math.min(...inRun.map((entry) => entry.x0));
    const right = Math.max(...inRun.map((entry) => entry.x1));
    const blockWidth = right - left;
    const byShelf = new Map<number, typeof inRun>();
    for (const entry of inRun)
      byShelf.set(entry.shelf, [...(byShelf.get(entry.shelf) ?? []), entry]);
    for (const list of byShelf.values()) list.sort((a, b) => a.x0 - b.x0);
    const shelfCount = byShelf.size;
    const levels = levelsForScope(draft.spine, draft.scopedLevelOrder, scopePath);
    const brandIndex = levels.findIndex((level) => level.attr === brandAttr);
    const lower = (brandIndex < 0 ? [] : levels.slice(brandIndex + 1))
      .map((level) => level.attr)
      .filter((attr) => attr !== quantityAttr && !structural.has(attr));

    /** The brand block's filled values of a level. */
    const valuesIn = (attr: string, skuIds: Iterable<string>) =>
      new Set(
        [...skuIds]
          .map((skuId) => String(attrsOf(skuId)?.[attr] ?? ""))
          .filter((value) => value !== "" && !isPlaceholderValue(value)),
      );
    const blockSkus = new Set(inRun.map((entry) => entry.skuId));
    /**
     * A yes/no level (two values in the block: Scented, HE) names a line only when no level with
     * three or more values covers the same column, that is, has the column's products as exactly
     * the products of some of its values. Tide's pods and tiles are all "HE safe", but the forms
     * already say what they are; Tide's 14 white liquids are no set of whole sub-brands or scents.
     */
    const yesNoCovered = (attr: string, column: readonly string[]): boolean => {
      if (valuesIn(attr, blockSkus).size > 2) return false;
      const inColumn = new Set(column);
      return levels.some(({ attr: other }) => {
        if (other === attr || other === brandAttr || other === quantityAttr) return false;
        if (valuesIn(other, blockSkus).size < 3) return false;
        const columnValues = valuesIn(other, column);
        if (columnValues.size === 0) return false;
        return [...blockSkus].every(
          (skuId) =>
            inColumn.has(skuId) === columnValues.has(String(attrsOf(skuId)?.[other] ?? "")),
        );
      });
    };

    const offers: Flag[] = [];
    for (const side of ["left", "right"] as const) {
      let found: EdgeColumn | null = null;
      for (const attr of lower) {
        const valueOf = (skuId: string) => String(attrsOf(skuId)?.[attr] ?? "");
        // The run of one value at this end of each shelf.
        const edgeRuns = new Map<string, { x0: number; x1: number; entries: typeof inRun }[]>();
        for (const list of byShelf.values()) {
          const ordered = side === "right" ? [...list].reverse() : list;
          const value = valueOf(ordered[0].skuId);
          if (!value || isPlaceholderValue(value)) continue;
          let x0 = ordered[0].x0;
          let x1 = ordered[0].x1;
          const entries: typeof inRun = [];
          for (const entry of ordered) {
            if (valueOf(entry.skuId) !== value) break;
            x0 = Math.min(x0, entry.x0);
            x1 = Math.max(x1, entry.x1);
            entries.push(entry);
          }
          edgeRuns.set(value, [...(edgeRuns.get(value) ?? []), { x0, x1, entries }]);
        }
        for (const [value, atEdge] of edgeRuns) {
          if (atEdge.length < 2 || atEdge.length * 2 <= shelfCount) continue;
          const colStart = Math.min(...atEdge.map((entry) => entry.x0));
          const colEnd = Math.max(...atEdge.map((entry) => entry.x1));
          if ((colEnd - colStart) * 3 > blockWidth) continue;
          const ofValue = inRun.filter((entry) => valueOf(entry.skuId) === value);
          const column = atEdge.flatMap((entry) => entry.entries);
          const skuIds = [...new Set(column.map((entry) => entry.skuId))].sort(
            (a, b) => (itemRank.get(a) ?? 0) - (itemRank.get(b) ?? 0),
          );
          const valueSkus = new Set(ofValue.map((entry) => entry.skuId)).size;
          if (skuIds.length < MIN_SPLIT_OFF_SKUS) continue;
          if (skuIds.length < valueSkus * EDGE_COLUMN_SHARE) continue;
          if (skuIds.length * 3 > brandSkus) continue;
          if (yesNoCovered(attr, skuIds)) continue;
          // The level and value whose column holds the most products names the line; on a tie
          // the shallower level, which comes first.
          if (found && found.skuIds.length >= skuIds.length) continue;
          const inColumn = new Set(skuIds);
          found = {
            attr,
            value,
            skuIds,
            shelves: atEdge.length,
            shelfCount,
            leftOut: valueSkus - skuIds.length,
            column: column.map((entry) => entry.placement),
            rest: inRun
              .filter((entry) => !inColumn.has(entry.skuId))
              .map((entry) => entry.placement),
          };
        }
      }
      // An existing brand must not be scattered, and products already reclassified must not
      // be offered again under a different attribute's name.
      if (
        found &&
        (brandValues.has(splitOffBrandName(brand, found.value)) ||
          found.skuIds.every((skuId) => String(attrsOf(skuId)?.[brandAttr] ?? "") !== brand))
      )
        found = null;
      if (found) offers.push(edgeFlag(group, side, found));
    }
    return offers;
  }

  function edgeFlag(group: BrandBlock, side: "left" | "right", found: EdgeColumn): Flag {
    const { scopePath, brand } = group;
    const { attr, value, skuIds, shelves, shelfCount, leftOut } = found;
    const newBrand = splitOffBrandName(brand, value);
    const id = offerIdOf(scopePath, newBrand);
    const declined = declinedIds.has(id);
    const where = baysText(found.column, runs);
    const count = skuIds.length;
    const them = count === 1 ? "it" : "them";
    return {
      id,
      kind: "split",
      severity: "blocking",
      scopePath,
      levelLabel: group.levelLabel ?? labelOf(brandAttr),
      title:
        `${scopePath.join(PATH_SEPARATOR)}: the ${where} column at its ${side} edge is all ` +
        `"${value}" → split off as brand "${newBrand}" (${count} ${count === 1 ? "SKU" : "SKUs"})`,
      detail:
        `On the base planogram, ${brand}'s ${value} ${products(count)} stand as a column of ` +
        `${count === 1 ? "its" : "their"} own at the ${side} edge of ${brand}'s block (${where}), ` +
        `on ${shelves} of ${shelfCount} shelves, beside the rest of ${brand}. A store planogram ` +
        `keeps a line as a block only when the CDT carries it as a brand, so splitting ${them} ` +
        `off as ${newBrand} lets ${newBrand} stand where the base puts it.` +
        (leftOut > 0
          ? ` ${leftOut} ${value} ${products(leftOut)} inside the rest of ${brand} ` +
            `${leftOut === 1 ? "stays" : "stay"} ${brand}.`
          : ""),
      skuIds,
      anchorSkuId: skuIds[0],
      evidence: {
        notes: [
          `${labelOf(attr)}: ${value}`,
          `Column at the ${side} edge: ${shelves} of ${shelfCount} shelves`,
        ],
      },
      fix: { type: "retagItems", attr: brandAttr, value: newBrand, skuIds },
      fixLabel: `Split off brand "${newBrand}"`,
      question: {
        brand,
        newBrand,
        skuCount: count,
        where: bayLocations(found.column, runs),
        mainWhere: bayLocations(found.rest, runs),
        shape: "edge",
        ...(declined ? { declined: true } : {}),
      },
    };
  }

  /**
   * Each new brand stands in the brand order where the base stands it: after the brands whose
   * first product comes before its own on the base walk, every offer taken, so all Baby counts
   * as a brand before ARM & HAMMER Baby and the pair keeps the base's order either way round.
   * Only the split-off's own section counts: Xtra powder in an earlier aisle says nothing about
   * where Xtra stands among the detergents.
   */
  function withBrandOrder(flags: Flag[]): Flag[] {
    const newBrandOf = new Map<string, string>();
    for (const flag of flags) {
      if (!isOffer(flag)) continue;
      for (const skuId of flag.fix.skuIds) newBrandOf.set(skuId, flag.fix.value);
    }
    if (newBrandOf.size === 0) return flags;
    const walk = basePogWalk(items, runs);
    /** Each brand's first position on the walk, over the products of one section. */
    const firstRankIn = (inSection: (skuId: string) => boolean): Map<string, number> => {
      const firstRank = new Map<string, number>();
      for (const item of items) {
        const rank = walk.rankOf.get(item.skuId);
        if (rank === undefined || item.placements.length === 0 || !inSection(item.skuId)) continue;
        const brand = newBrandOf.get(item.skuId) ?? String(attrsOf(item.skuId)?.[brandAttr] ?? "");
        if (isPlaceholderValue(brand)) continue;
        if (rank < (firstRank.get(brand) ?? Infinity)) firstRank.set(brand, rank);
      }
      return firstRank;
    };
    const bySection = new Map<string, Map<string, number>>();
    return flags.map((flag) => {
      if (!isOffer(flag)) return flag;
      // The section is the flag's path above the brand, read the way the flag's path is shown:
      // the values of the levels above the brand, blank ones left out.
      const levels = levelsForScope(draft.spine, draft.scopedLevelOrder, flag.scopePath);
      const brandIndex = levels.findIndex((level) => level.attr === brandAttr);
      const above = brandIndex < 0 ? null : levels.slice(0, brandIndex).map((level) => level.attr);
      const section = flag.scopePath.slice(0, -1);
      const key = `${above?.join("|") ?? "*"}${PATH_SEPARATOR}${section.join(PATH_SEPARATOR)}`;
      let firstRank = bySection.get(key);
      if (!firstRank) {
        firstRank = firstRankIn((skuId) => {
          if (!above) return true;
          const attrs = attrsOf(skuId);
          const path = above
            .map((attr) => String(attrs?.[attr] ?? "").trim())
            .filter((segment) => segment.length > 0);
          return path.length === section.length && path.every((value, i) => value === section[i]);
        });
        bySection.set(key, firstRank);
      }
      const own = firstRank.get(flag.fix.value) ?? Infinity;
      const after = [...firstRank.entries()]
        .filter(([, rank]) => rank < own)
        .sort((a, b) => a[1] - b[1])
        .map(([brand]) => brand);
      return { ...flag, fix: { ...flag.fix, order: { from: flag.question.brand, after } } };
    });
  }

  function splitOffFlag(
    flag: Flag,
    brand: string,
    main: ReadonlySet<string>,
    mainWhere: FlagQuestion["mainWhere"],
    explained: Explained,
    declined: boolean,
  ): Flag {
    const { evidence, skuIds, stretchCount, vouchers } = explained;
    const value = evidence.value;
    const newBrand = splitOffBrandName(brand, value);
    const where = whereText(skuIds, bySku, runs);
    const byName = evidence.source === "name";
    const count = skuIds.length;
    const carries = (skuId: string) =>
      evidence.source === "attr"
        ? attrsOf(skuId)?.[evidence.attr] === value
        : nameWords(skuId, brand).has(value.toLowerCase());
    const leftInMain = [...main].filter(carries).length;
    const voucher = vouchers[0];

    // "the bay 8–9 stretch is all "Simply"" for a stretch, "the bay 5 product is "Baby"" for one.
    const subject =
      stretchCount > 1
        ? `the ${where} stretches are all`
        : count >= MIN_SPLIT_OFF_SKUS
          ? `the ${where} stretch is all`
          : `the ${where} ${products(count)} ${count === 1 ? "is" : "are"}`;
    const beside = voucher ? `, beside ${voucher.brand}'s "${voucher.value}"` : "";

    const them = count === 1 ? "it" : "them";
    const own = count === 1 ? "its" : "their";
    const said = byName
      ? `the product name, which says ${value}`
      : `${labelOf(evidence.source === "attr" ? evidence.attr : "")} ${value}`;

    const detail = voucher
      ? `A shopper finds ${brand}'s ${value} ${products(count)} at ${where}, away from the rest ` +
        `of ${brand} and beside ${voucher.brand}'s ${voucher.value}. The evidence is ${said}. ` +
        `The ${voucher.brand} product beside ${them} (${voucher.skuId}) is ${voucher.value}` +
        `${voucher.byName ? " by name" : ""} too, so the base stands them together as one ` +
        `${value} group rather than as strays.`
      : `Foreign product stands between ${brand}'s stretches, and every product in the ` +
        `${where} ${stretchCount > 1 ? "stretches" : "stretch"} is ${value}, so the tree ` +
        `cannot rebuild ${brand} as one block.` +
        (byName
          ? ` The evidence is the product names: every one says ${value}, and no attribute does.`
          : "");

    return {
      ...flag,
      id: offerIdOf(flag.scopePath, newBrand),
      // The plain finding's own wording leads, so the row reads as that violation, explained.
      title:
        `${flag.title}; ${subject} "${value}"${byName ? " by name" : ""}${beside} → ` +
        `split off as brand "${newBrand}" (${count} ${count === 1 ? "SKU" : "SKUs"})`,
      detail:
        detail +
        ` Splitting ${them} off as ${own} own brand, ${newBrand}, makes ${brand} one block and lets ` +
        `${newBrand} stand where the base puts it.` +
        (leftInMain > 0
          ? ` ${leftInMain} ${value} ${products(leftInMain)} inside ${brand}'s main stretch ` +
            `${leftInMain === 1 ? "stays" : "stay"} ${brand}.`
          : ""),
      skuIds,
      anchorSkuId: skuIds[0],
      evidence: {
        notes: [
          byName ? `Product name: ${value}` : `${labelOf(evidence.attr)}: ${value}`,
          ...vouchers.map((entry) => `Beside: ${entry.brand} "${entry.value}" (${entry.skuId})`),
        ],
      },
      fix: { type: "retagItems", attr: brandAttr, value: newBrand, skuIds },
      fixLabel: `Split off brand "${newBrand}"`,
      question: {
        brand,
        newBrand,
        skuCount: count,
        where: bayLocations(
          skuIds.flatMap((skuId) => bySku.get(skuId)?.placements ?? []),
          runs,
        ),
        mainWhere,
        shape: "detached",
        ...(declined ? { declined: true } : {}),
        ...(voucher
          ? {
              voucher: {
                brand: voucher.brand,
                newBrand: splitOffBrandName(voucher.brand, voucher.value),
                skuId: voucher.skuId,
              },
            }
          : {}),
      },
    };
  }
};

/**
 * An explained product is no stray: a "sits N bays from its block" row or a "scattered across N
 * places" row whose every product a split-off offer covers goes, with its pin. A scatter row that
 * still holds a product no offer covers stays: that scatter is still real. An offer the merchant
 * declined explains nothing, so its products keep their rows.
 */
export const dropExplainedStrays = (
  flags: readonly Flag[],
  pinByFlagId: Readonly<Record<string, ResolverPin>>,
  scatterFlagIds: ReadonlySet<string>,
): { flags: Flag[]; pinByFlagId: Record<string, ResolverPin> } => {
  const covered = new Set(
    flags.flatMap((flag) =>
      flag.id.startsWith("split-off:") &&
      flag.fix?.type === "retagItems" &&
      flag.question?.declined !== true
        ? flag.fix.skuIds
        : [],
    ),
  );
  if (covered.size === 0) return { flags: [...flags], pinByFlagId: { ...pinByFlagId } };
  const pins = { ...pinByFlagId };
  const kept = flags.filter((flag) => {
    const stray =
      (flag.kind === "orphan" &&
        flag.skuIds.length > 0 &&
        flag.skuIds.every((skuId) => covered.has(skuId))) ||
      (scatterFlagIds.has(flag.id) &&
        flag.skuIds.length > 0 &&
        flag.skuIds.every((skuId) => covered.has(skuId)));
    if (stray) delete pins[flag.id];
    return !stray;
  });
  return { flags: kept, pinByFlagId: pins };
};
